import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserBookings, supabaseServiceRole, updateBookingStatus, getUserDiscount } from '../services/supabase';
import { Database } from '../types/supabase';
import { Button } from '../components/ui/Button';
import toast from 'react-hot-toast';
import EditBookingForm from '../components/booking/EditBookingForm';

type Booking = Database['public']['Tables']['bookings']['Row'] & {
    equipment: Database['public']['Tables']['equipment']['Row'][] | Database['public']['Tables']['equipment']['Row'];
};

type User = {
    id: string;
    telegram_id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    role: string;
    created_at: string;
    updated_at: string | null;
};

// Компонент для отображения статуса бронирования
const BookingStatusBadge = ({ status }: { status: string }) => {
    let colorClasses = '';
    let statusText = '';

    switch (status) {
        case 'confirmed':
            colorClasses = 'bg-green-100 text-green-800';
            statusText = 'Подтверждено';
            break;
        case 'pending':
            colorClasses = 'bg-amber-100 text-amber-800';
            statusText = 'Ожидает подтверждения';
            break;
        case 'completed':
            colorClasses = 'bg-blue-100 text-blue-800';
            statusText = 'Завершено';
            break;
        case 'cancelled':
        case 'canceled':
            colorClasses = 'bg-red-100 text-red-800';
            statusText = 'Отменено';
            break;
        default:
            colorClasses = 'bg-gray-100 text-gray-800';
            statusText = status;
    }

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses}`}>
            {statusText}
        </span>
    );
};

// Функция для форматирования даты
const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
};

// Функция для получения текста для дней
const getDaysText = (days: number) => {
    const lastDigit = days % 10;
    const lastTwoDigits = days % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'дней';
    }

    if (lastDigit === 1) {
        return 'день';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'дня';
    }

    return 'дней';
};

const ProfilePage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [cancelingBookingId, setCancelingBookingId] = useState<string | null>(null);
    const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
    const [userDiscount, setUserDiscount] = useState<number>(0);
    const queryClient = useQueryClient();

    // Загружаем данные пользователя из локального хранилища
    useEffect(() => {
        const loadUserData = async () => {
            // Попробуем загрузить в формате 'user'
            let userStr = localStorage.getItem('user');
            console.log('Данные пользователя из localStorage:', userStr);

            // Если нет, попробуем загрузить в формате 'telegram_user'
            if (!userStr) {
                userStr = localStorage.getItem('telegram_user');
                console.log('Данные пользователя из telegram_user:', userStr);
            }

            if (!userStr) {
                // Если пользователь не авторизован, перенаправляем на страницу логина
                console.log('Пользователь не найден в localStorage, перенаправление на /login');
                navigate('/login');
                return;
            }

            try {
                const parsedUser = JSON.parse(userStr);
                console.log('Распарсенные данные пользователя:', parsedUser);

                // Преобразуем формат telegram_user в стандартный формат, если это необходимо
                if (!parsedUser.id && parsedUser.id !== 0 && parsedUser.user_id) {
                    parsedUser.id = parsedUser.user_id;
                    console.log('Преобразован формат ID пользователя:', parsedUser.id);
                }

                // Сохраняем в формате 'user' для единообразия
                if (userStr === localStorage.getItem('telegram_user')) {
                    localStorage.setItem('user', userStr);
                }

                setUser(parsedUser as User);
                console.log('Данные пользователя установлены в state:', parsedUser);
            } catch (error) {
                console.error('Ошибка загрузки данных пользователя:', error);
                navigate('/login');
            }
        };

        loadUserData();
    }, [navigate]);

    // Получаем бронирования пользователя
    const { data: bookings, isLoading, refetch } = useQuery({
        queryKey: ['bookings', user?.id],
        queryFn: async () => {
            if (!user?.id) {
                console.log('ID пользователя не найден, бронирования не загружаются');
                return [];
            }

            console.log('Начало загрузки бронирований для пользователя:', user.id);

            try {
                // Прямой запрос к Supabase с правильной связью таблиц
                console.log('Выполняем прямой запрос к таблицам с правильной связью...');
                const { data: directData, error: directError } = await supabaseServiceRole
                    .from('bookings')
                    .select(`
                        *,
                        booking_equipment!booking_equipment_booking_id_fkey (
                            *,
                            equipment!booking_equipment_equipment_id_fkey (*)
                        )
                    `)
                    .eq('user_id', user.id);

                if (directError) {
                    console.error('Ошибка при прямом запросе бронирований:', directError);
                    return [];
                }

                console.log('Получены данные прямым запросом:', directData);

                // Преобразуем данные в нужный формат
                const formattedData = directData.map(booking => {
                    // Извлекаем оборудование из вложенных объектов и добавляем quantity из booking_equipment
                    const equipment = booking.booking_equipment.map(item => ({
                        ...item.equipment,
                        quantity: item.quantity
                    }));
                    return {
                        ...booking,
                        equipment
                    };
                });

                console.log('Отформатированные данные бронирований:', formattedData);
                return formattedData as unknown as Booking[];
            } catch (error) {
                console.error('Непредвиденная ошибка при загрузке бронирований:', error);

                // Запасной вариант - используем существующую функцию
                console.log('Используем запасной вариант через getUserBookings...');
                try {
                    const { data, error } = await getUserBookings(user.id);
                    if (error) {
                        console.error('Ошибка при получении бронирований через getUserBookings:', error);
                        return [];
                    }
                    console.log('Данные, полученные через getUserBookings:', data);
                    return data as unknown as Booking[];
                } catch (fallbackError) {
                    console.error('Ошибка при использовании запасного варианта:', fallbackError);
                    return [];
                }
            }
        },
        enabled: !!user?.id,
    });

    // Добавим эффект для отслеживания изменений в данных бронирований
    useEffect(() => {
        console.log('Данные бронирований обновлены:', bookings);

        if (bookings && bookings.length > 0) {
            console.log('Количество бронирований:', bookings.length);
            console.log('Первое бронирование:', bookings[0]);

            // Проверим наличие оборудования в первом бронировании
            if (bookings[0].equipment) {
                if (Array.isArray(bookings[0].equipment)) {
                    console.log('Оборудование (массив):', bookings[0].equipment);
                } else {
                    console.log('Оборудование (объект):', bookings[0].equipment);
                }
            } else {
                console.log('Оборудование отсутствует в бронировании');
            }
        }
    }, [bookings]);

    // Получаем информацию о скидке пользователя при монтировании компонента
    useEffect(() => {
        const fetchUserDiscount = async () => {
            try {
                console.log('🔵 Запрос скидки для пользователя:', user?.id, user?.username);
                const { data, error } = await getUserDiscount();
                if (error) {
                    console.error('🔴 Ошибка при получении скидки:', error);
                } else if (data !== undefined) {
                    console.log('🟢 Получена скидка пользователя в ProfilePage:', data, 'для пользователя с ролью:', user?.role);
                    setUserDiscount(data);
                } else {
                    console.warn('🟡 Получены неопределенные данные о скидке');
                }
            } catch (error) {
                console.error('🔴 Непредвиденная ошибка при получении скидки:', error);
            }
        };

        if (user) {
            fetchUserDiscount();
        }
    }, [user]);

    const handleLogout = async () => {
        try {
            // Удаляем данные пользователя из всех возможных мест
            localStorage.removeItem('user');
            localStorage.removeItem('telegram_user');

            // Перенаправляем на страницу логина
            navigate('/login');
        } catch (error) {
            console.error('Ошибка при выходе:', error);
        }
    };

    // Функция для расчета общей стоимости бронирования
    const calculateTotalPrice = (booking: Booking) => {
        if (!booking.equipment || !booking.start_date || !booking.end_date) {
            return 0;
        }

        const startDate = new Date(booking.start_date);
        const endDate = new Date(booking.end_date);
        const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1;

        if (Array.isArray(booking.equipment)) {
            return booking.equipment.reduce((total, item) => {
                const price = item.daily_rate * (item.quantity || 1) * days;
                return total + price;
            }, 0);
        } else {
            return booking.equipment.daily_rate * (booking.equipment.quantity || 1) * days;
        }
    };

    // Функция для отмены бронирования
    const handleCancelBooking = async (bookingId: string) => {
        if (!confirm('Вы уверены, что хотите отменить это бронирование?')) {
            return;
        }

        setCancelingBookingId(bookingId);

        try {
            const { error } = await updateBookingStatus(bookingId, 'cancelled');

            if (error) {
                toast.error('Не удалось отменить бронирование');
                console.error('Ошибка при отмене бронирования:', error);
                return;
            }

            toast.success('Бронирование успешно отменено');

            // Инвалидируем кеш доступного оборудования
            queryClient.invalidateQueries({ queryKey: ['availableEquipment'] });
            queryClient.invalidateQueries({ queryKey: ['equipment'] });

            // Обновляем список бронирований
            refetch();
        } catch (err) {
            toast.error('Произошла ошибка при отмене бронирования');
            console.error('Ошибка при отмене бронирования:', err);
        } finally {
            setCancelingBookingId(null);
        }
    };

    // Функция для завершения бронирования (для членов команды)
    const [completingBookingId, setCompletingBookingId] = useState<string | null>(null);

    const handleCompleteBooking = async (bookingId: string) => {
        if (!confirm('Вы уверены, что хотите завершить это бронирование?')) {
            return;
        }

        setCompletingBookingId(bookingId);

        try {
            const { error } = await updateBookingStatus(bookingId, 'completed');

            if (error) {
                toast.error('Не удалось завершить бронирование');
                console.error('Ошибка при завершении бронирования:', error);
                return;
            }

            toast.success('Бронирование успешно завершено');

            // Инвалидируем кеш доступного оборудования
            queryClient.invalidateQueries({ queryKey: ['availableEquipment'] });
            queryClient.invalidateQueries({ queryKey: ['equipment'] });

            // Обновляем список бронирований
            refetch();
        } catch (err) {
            toast.error('Произошла ошибка при завершении бронирования');
            console.error('Ошибка при завершении бронирования:', err);
        } finally {
            setCompletingBookingId(null);
        }
    };

    // Функция для подтверждения бронирования (для членов команды)
    const [confirmingBookingId, setConfirmingBookingId] = useState<string | null>(null);

    const handleConfirmBooking = async (bookingId: string) => {
        if (!confirm('Вы уверены, что хотите подтвердить это бронирование?')) {
            return;
        }

        setConfirmingBookingId(bookingId);

        try {
            const { error } = await updateBookingStatus(bookingId, 'confirmed');

            if (error) {
                toast.error('Не удалось подтвердить бронирование');
                console.error('Ошибка при подтверждении бронирования:', error);
                return;
            }

            toast.success('Бронирование успешно подтверждено');

            // Инвалидируем кеш доступного оборудования
            queryClient.invalidateQueries({ queryKey: ['availableEquipment'] });
            queryClient.invalidateQueries({ queryKey: ['equipment'] });

            // Обновляем список бронирований
            refetch();
        } catch (err) {
            toast.error('Произошла ошибка при подтверждении бронирования');
            console.error('Ошибка при подтверждении бронирования:', err);
        } finally {
            setConfirmingBookingId(null);
        }
    };

    // Функция для редактирования бронирования
    const handleEditBooking = (booking: Booking) => {
        // Проверяем, что бронирование не завершено и не отменено
        if (booking.status === 'completed' || booking.status === 'cancelled') {
            toast.error('Нельзя редактировать завершенное или отмененное бронирование');
            return;
        }

        setEditingBooking(booking);
    };

    // Функция закрытия формы редактирования
    const handleCloseEditForm = () => {
        setEditingBooking(null);
    };

    // Функция успешного обновления бронирования
    const handleEditSuccess = () => {
        setEditingBooking(null);
        refetch();
    };

    if (!user) {
        return (
            <div className="flex justify-center items-center min-h-[70vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 bg-white">
            <h1 className="text-2xl font-semibold text-gray-800 mb-6">Личный кабинет</h1>

            {/* Информация о пользователе */}
            <div className="bg-white border border-primary-100 shadow-sm rounded-apple p-6 mb-8">
                <div className="flex items-center">
                    <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center text-primary-500 font-bold text-xl mr-4">
                        {user.first_name?.charAt(0).toUpperCase() || user.username?.charAt(0).toUpperCase() || 'У'}
                    </div>
                    <div className="flex-1">
                        <h2 className="text-xl font-medium text-gray-800">
                            {user.first_name} {user.last_name || ''}
                            {!user.first_name && !user.last_name && user.username && `@${user.username}`}
                            {!user.first_name && !user.last_name && !user.username && 'Пользователь'}
                        </h2>
                        {user.username && <p className="text-gray-600">@{user.username}</p>}
                        <p className="text-sm text-gray-500 mt-1">
                            Дата регистрации: {new Date(user.created_at).toLocaleDateString('ru-RU')}
                        </p>
                        <div className="text-sm text-gray-600 mt-1 flex items-center">
                            <span className="inline-block px-2 py-0.5 rounded-md bg-primary-50 text-primary-700 font-medium capitalize mr-1">
                                {user.role === 'franema_member' ? 'Член команды' : user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                            </span>

                            {userDiscount > 0 && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
                                    Скидка {userDiscount}%
                                </span>
                            )}
                        </div>
                    </div>
                    <div>
                        <Button
                            variant="destructive"
                            onClick={handleLogout}
                        >
                            Выйти
                        </Button>
                    </div>
                </div>

                {user.role === 'admin' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <Button
                            variant="primary"
                            onClick={() => navigate('/admin')}
                        >
                            Перейти в админ-панель
                        </Button>
                    </div>
                )}
            </div>

            {/* История бронирований */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4">История бронирований</h2>

            {isLoading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : bookings && bookings.length > 0 ? (
                <div className="overflow-hidden border border-primary-100 shadow-sm rounded-apple mb-6">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Период</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Оборудование</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Проект</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Стоимость</th>
                                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Действия</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {[...bookings]
                                // Сортируем сначала по статусу, затем внутри каждого статуса по дате начала бронирования
                                .sort((a, b) => {
                                    // Порядок статусов: confirmed, pending, completed, cancelled
                                    const statusOrder: Record<string, number> = {
                                        confirmed: 1,
                                        pending: 2,
                                        completed: 3,
                                        cancelled: 4
                                    };

                                    // Сначала сортируем по статусу
                                    const statusA = a.status || '';
                                    const statusB = b.status || '';

                                    if (statusA !== statusB) {
                                        return (statusOrder[statusA] || 999) - (statusOrder[statusB] || 999);
                                    }

                                    // Если статусы одинаковые, сортируем по дате начала бронирования
                                    const startDateA = new Date(a.start_date).getTime();
                                    const startDateB = new Date(b.start_date).getTime();
                                    const currentDate = new Date().getTime();

                                    // Получаем разницу между текущей датой и датой начала бронирования
                                    // (ближайшие даты в начале)
                                    // Для прошедших дат - сортируем от более близких к более далеким в прошлом
                                    // Для будущих дат - также от более близких к более далеким в будущем
                                    const diffA = Math.abs(startDateA - currentDate);
                                    const diffB = Math.abs(startDateB - currentDate);

                                    return diffA - diffB;
                                })
                                .map((booking) => {
                                    const startDate = new Date(booking.start_date);
                                    const endDate = new Date(booking.end_date);
                                    const days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1;
                                    const totalPrice = booking.total_price || calculateTotalPrice(booking);

                                    const equipment = Array.isArray(booking.equipment)
                                        ? booking.equipment
                                        : booking.equipment ? [booking.equipment] : [];

                                    return (
                                        <tr key={booking.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm">
                                                <div className="font-medium text-gray-800">
                                                    #{booking.id.substring(0, 8)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {formatDate(booking.created_at || '')}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-700">
                                                <div>{formatDate(booking.start_date)}</div>
                                                <div className="text-xs">— {formatDate(booking.end_date)}</div>
                                                <div className="text-xs text-gray-500">({days} {getDaysText(days)})</div>
                                            </td>
                                            <td className="px-4 py-4 text-sm">
                                                <div className="space-y-1">
                                                    {equipment.map((item, idx) => (
                                                        <div key={idx} className="flex items-center">
                                                            {item.image_url ? (
                                                                <img
                                                                    src={item.image_url}
                                                                    alt={item.name}
                                                                    className="w-8 h-8 object-cover rounded-md mr-2"
                                                                />
                                                            ) : (
                                                                <div className="w-8 h-8 bg-gray-100 rounded-md mr-2"></div>
                                                            )}
                                                            <div className="truncate max-w-[150px]" title={item.name}>
                                                                {item.name}
                                                                <span className="text-xs text-gray-500 ml-1">
                                                                    × {item.quantity || 1}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-700">
                                                {booking.project_name ? (
                                                    <div className="font-medium">{booking.project_name}</div>
                                                ) : (
                                                    <div className="text-gray-500">—</div>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <BookingStatusBadge status={booking.status || ''} />
                                            </td>
                                            <td className="px-4 py-4 text-right font-medium text-yellow-600">
                                                {totalPrice.toLocaleString('ru-RU')} ₽
                                            </td>
                                            <td className="px-4 py-4 text-right">
                                                <div className="flex flex-col space-y-1">
                                                    {(booking.status === 'confirmed' || booking.status === 'pending') && (
                                                        <>
                                                            <button
                                                                onClick={() => handleEditBooking(booking)}
                                                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                                            >
                                                                Изменить
                                                            </button>
                                                            <button
                                                                onClick={() => handleCancelBooking(booking.id)}
                                                                disabled={cancelingBookingId === booking.id}
                                                                className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                                            >
                                                                {cancelingBookingId === booking.id ? 'Отмена...' : 'Отменить'}
                                                            </button>

                                                            {/* Кнопка завершения для членов команды */}
                                                            {user?.role === 'franema_member' && booking.status === 'confirmed' && (
                                                                <button
                                                                    onClick={() => handleCompleteBooking(booking.id)}
                                                                    disabled={completingBookingId === booking.id}
                                                                    className="text-sm text-green-600 hover:text-green-800 font-medium disabled:opacity-50 mt-1"
                                                                >
                                                                    {completingBookingId === booking.id ? 'Завершение...' : 'Завершить'}
                                                                </button>
                                                            )}

                                                            {/* Кнопка подтверждения для членов команды */}
                                                            {user?.role === 'franema_member' && booking.status === 'pending' && (
                                                                <button
                                                                    onClick={() => handleConfirmBooking(booking.id)}
                                                                    disabled={confirmingBookingId === booking.id}
                                                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 mt-1"
                                                                >
                                                                    {confirmingBookingId === booking.id ? 'Подтверждение...' : 'Подтвердить'}
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-gray-50 p-8 rounded-md text-center mb-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">У вас еще нет бронирований</h3>
                    <p className="text-gray-500 mb-6">Выберите и забронируйте необходимое оборудование</p>
                    <Button to="/equipment">Перейти к оборудованию</Button>
                </div>
            )}

            {/* Модальное окно для редактирования бронирования */}
            {editingBooking && (
                <EditBookingForm
                    bookingId={editingBooking.id}
                    startDate={editingBooking.start_date}
                    endDate={editingBooking.end_date}
                    projectName={editingBooking.project_name}
                    equipment={Array.isArray(editingBooking.equipment)
                        ? editingBooking.equipment.map(item => ({
                            id: item.id,
                            name: item.name,
                            quantity: item.quantity || 1,
                            dailyRate: item.daily_rate,
                            image_url: item.image_url || undefined
                        }))
                        : editingBooking.equipment
                            ? [{
                                id: editingBooking.equipment.id,
                                name: editingBooking.equipment.name,
                                quantity: editingBooking.equipment.quantity || 1,
                                dailyRate: editingBooking.equipment.daily_rate,
                                image_url: editingBooking.equipment.image_url || undefined
                            }]
                            : []
                    }
                    onClose={handleCloseEditForm}
                    onSuccess={handleEditSuccess}
                />
            )}
        </div>
    );
};

export default ProfilePage;